import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ArrowUpRightIcon, ChevronDownIcon, CheckCircle2Icon, HeartIcon } from 'lucide-react';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
import { OrgLogo } from './shared/OrgLogo';
const NAVY = '#0b1f3f';
const BLUE = '#1565C0';
const RED = '#cf142b';
const EASE = [0.23, 1, 0.32, 1] as const;
interface NetworkOrganizationsProps {
  isEs: boolean;
}

/**
 * Expandable spotlight list for organizations that have supplied a logo.
 * Collapsed, each row shows only the logo, name, and location; expanding
 * reveals the mission, leadership, and where the organization works.
 */
export function NetworkOrganizations({
  isEs
}: NetworkOrganizationsProps) {
  const orgs = SISTER_ORGANIZATIONS.filter(o => o.logo || o.mission);
  const [open, setOpen] = useState<string | null>(orgs[0]?.name ?? null);
  if (orgs.length === 0) return null;
  return <ul className="flex flex-col gap-4" data-id="element-3051" data-mp="3comcvviieo8f">
      {orgs.map(org => {
      const isOpen = open === org.name;
      const panelId = `org-panel-${org.name.replace(/\s+/g, '-')}`;
      return <li key={org.name} className="rounded-2xl border bg-white overflow-hidden" style={{
        borderColor: 'rgba(11,31,63,0.14)'
      }} data-id="element-3052" data-mp="25xexqzp1bzvv">
            {org.registered501c3 !== false && <div className="flex items-center gap-1.5 px-5 sm:px-6 py-2 border-b" style={{
          backgroundColor: 'rgba(22,163,74,0.07)',
          borderColor: 'rgba(11,31,63,0.1)'
        }} data-id="element-3053" data-mp="1i6j9gcjsfmtd">
                <CheckCircle2Icon className="w-3.5 h-3.5 flex-shrink-0" style={{
            color: '#16a34a'
          }} aria-hidden="true" data-id="element-3054" data-mp="1r4v0gfokgmmc" />
                <span className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em]" style={{
            color: '#15803d'
          }} data-id="element-3055" data-mp="yg64sw6a59hz">
                  {isEs ? 'Organización 501(c)(3) Registrada' : 'Registered 501(c)(3)'}
                </span>
              </div>}

            <button type="button" onClick={() => setOpen(isOpen ? null : org.name)} aria-expanded={isOpen} aria-controls={panelId} className="w-full flex items-center gap-5 p-5 sm:p-6 text-left transition-colors duration-200 hover:bg-[#f5f8fc]" data-id="element-3056" data-mp="2fj1ae015b2pu">
              {org.logo ? <OrgLogo src={org.logo} alt={`${org.name} logo`} crop={org.logoCrop} scale={org.logoScale} filter={org.logoFilter} boost={org.logoBoost} className="w-32 h-20 sm:w-56 sm:h-28 flex-shrink-0 rounded-xl bg-white" lazy data-id="element-3057" data-mp="1wtl93ahmz68d" /> : <div aria-hidden="true" className="w-32 h-20 sm:w-56 sm:h-28 flex-shrink-0 rounded-xl flex items-center justify-center font-bebas text-3xl sm:text-4xl tracking-[0.04em] text-white" style={{
            backgroundColor: org.accent
          }} data-id="element-3058" data-mp="3bqo3qphjzojx">
                  {monogram(org.name)}
                </div>}
              <div className="min-w-0 flex-1" data-id="element-3059" data-mp="3uhnotflxge7x">
                <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5" style={{
              color: BLUE
            }} data-id="element-3060" data-mp="3uhxcuxnewbaz">
                  {isEs ? org.focusEs : org.focus}
                </p>
                <h3 className="font-bebas text-2xl sm:text-3xl tracking-[0.03em] leading-tight mb-1.5" style={{
              color: NAVY
            }} data-id="element-3061" data-mp="3pqfemhcrwotp">
                  {org.name}
                </h3>
                <p className="inline-flex items-center gap-1.5 font-nunito text-xs uppercase tracking-[0.12em]" style={{
              color: 'rgba(11,31,63,0.55)'
            }} data-id="element-3062" data-mp="m2ndhxawpk6w">
                  <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                color: RED
              }} data-id="element-3063" data-mp="2pisfq4nykib1" />
                  {org.location}
                  {org.serviceArea ? ` · ${isEs ? org.serviceAreaEs : org.serviceArea}` : ''}
                </p>
              </div>
              <ChevronDownIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-200" style={{
            color: NAVY,
            transform: isOpen ? 'rotate(180deg)' : 'none'
          }} aria-hidden="true" data-id="element-3064" data-mp="38a352he1cn0t" />
            </button>

            <AnimatePresence initial={false} data-id="element-3065" data-mp="3vzxbag3undc3">
              {isOpen ? <motion.div id={panelId} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.26,
            ease: EASE
          }} className="overflow-hidden" data-id="element-3066" data-mp="3r9791bqpzqlm">
                  <div className="border-t" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3067" data-mp="cbamqgxk6g8s">
                    <div className="grid md:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-3068" data-mp="ldtjqkenxyn4">
                      {/* Mission + where they work */}
                      <div className="px-5 sm:px-7 py-7" data-id="element-3069" data-mp="qb2852qemywt">
                        <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                    color: 'rgba(11,31,63,0.4)'
                  }} data-id="element-3070" data-mp="v2tokkfujo98">
                          {isEs ? 'Misión' : 'Mission'}
                        </p>
                        <p className="font-nunito text-[17px] leading-[1.65] mb-7" style={{
                    color: 'rgba(11,31,63,0.82)'
                  }} data-id="element-3071" data-mp="192cvxb0u2vkh">
                          {isEs ? org.missionEs || org.descriptionEs : org.mission || org.description}
                        </p>

                        {org.partnerSites && org.partnerSites.length > 0 ? <div className="pt-6 border-t" style={{
                    borderColor: 'rgba(11,31,63,0.1)'
                  }} data-id="element-3072" data-mp="3ou9vibvktw5e">
                            <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                      color: 'rgba(11,31,63,0.4)'
                    }} data-id="element-3073" data-mp="3jxjffojrv0jz">
                              {isEs ? 'Su trabajo en Honduras' : 'Their work in Honduras'}
                            </p>
                            <ul className="flex flex-col gap-2" data-id="element-3074" data-mp="1pavcvskjunxc">
                              {org.partnerSites.map(site => <li key={site.name} className="inline-flex items-center gap-2 font-nunito text-sm font-bold" style={{
                        color: NAVY
                      }} data-id="element-3075" data-mp="1tbp1yp9qa8rt">
                                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" style={{
                          color: RED
                        }} data-id="element-3076" data-mp="15mcubtu1u8rm" />
                                  {site.location}
                                </li>)}
                            </ul>
                          </div> : null}
                      </div>

                      {/* Leadership panel */}
                      {org.director ? <div className="px-5 sm:px-7 py-7 border-t md:border-t-0 md:border-l flex flex-col" style={{
                  borderColor: 'rgba(11,31,63,0.1)',
                  backgroundColor: '#f6f9fc'
                }} data-id="element-3077" data-mp="2vxr5mpzynx0b">
                          <div className="flex items-center gap-5" data-id="element-3078" data-mp="3qtnvvjn41mwk">
                            {/* Constant portrait frame — every organization's
                                leadership photo renders at the same size and
                                crop regardless of the source image. */}
                            <div className="w-44 h-56 sm:w-52 sm:h-64 flex-shrink-0 rounded-lg overflow-hidden bg-white p-2" style={{
                      border: '1px solid rgba(11,31,63,0.16)',
                      boxShadow: '0 1px 3px rgba(11,31,63,0.08)'
                    }} data-id="element-3079" data-mp="3butudrz0erlv">
                              <img src={org.director.photo} alt={org.director.name || `${org.name} ${org.director.title}`} loading="lazy" decoding="async" className="w-full h-full rounded object-cover object-top" data-id="element-3080" data-mp="2o11sjvr702p8" />
                            </div>
                            <div className="min-w-0" data-id="element-3081" data-mp="2a2le1in397wj">
                              {org.director.name ? <p className="font-bebas text-2xl tracking-[0.03em] leading-tight" style={{
                        color: NAVY
                      }} data-id="element-3082" data-mp="2e4fbzj5wfxt0">
                                  {org.director.name}
                                </p> : null}
                              <p className="font-nunito text-[11px] font-bold uppercase tracking-[0.16em] mt-1" style={{
                        color: BLUE
                      }} data-id="element-3083" data-mp="20nfaihdbnauf">
                                {isEs ? org.director.titleEs : org.director.title}
                              </p>
                              {org.director.linkedin ? <a href={org.director.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-3084" data-mp="1mo5rl9xqe2mr">
                                  LinkedIn
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3085" data-mp="vgnoh2819pw3" />
                                </a> : null}
                              {org.director.profileUrl ? <a href={org.director.profileUrl} className="flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-3086" data-mp="mmr700ptd1n0">
                                  {isEs ? 'Ver Perfil' : 'View Profile'}
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3087" data-mp="3uxcbip5nty7u" />
                                </a> : null}
                            </div>
                          </div>
                        </div> : null}
                    </div>

                    {/* Actions — matched buttons across the full width */}
                    {org.donateUrl || org.url || org.extraLinks?.length ? <div className="px-5 sm:px-7 py-5 border-t flex flex-col sm:flex-row gap-3" style={{
                borderColor: 'rgba(11,31,63,0.1)'
              }} data-id="element-3088" data-mp="2h23zr035q1y5">
                        {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: RED
                }} data-id="element-3089" data-mp="2vyuxl4x6n5ln">
                            {isEs ? 'Visitar sitio web' : 'Visit Website'}
                            <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3090" data-mp="17z99fwfddn0i" />
                          </a> : null}
                        {org.donateUrl ? <a href={org.donateUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: '#4FA8E8'
                }} data-id="element-3091" data-mp="1h0h1hwralrm5">
                            <HeartIcon className="w-3.5 h-3.5" data-id="element-3092" data-mp="tacv5hmpzdrg" />
                            {isEs ? 'Donar / Participar' : 'Donate / Get Involved'}
                          </a> : null}
                        {org.extraLinks?.map(link => <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: NAVY
                }} data-id="element-3093" data-mp="ea90ybt0g439">
                            {isEs ? link.labelEs : link.label}
                            <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3094" data-mp="16ywoke18f5jz" />
                          </a>)}
                      </div> : null}
                  </div>
                </motion.div> : null}
            </AnimatePresence>
          </li>;
    })}
    </ul>;
}

/** Two-letter stand-in shown until an organization supplies its logo. */
function monogram(name: string): string {
  // Acronym-led names ("ASJ — …", "CRC …") use the acronym itself.
  const first = name.split(/\s/)[0];
  if (/^[A-Z]{2,4}$/.test(first)) return first;
  const words = name.split(/[\s—-]+/).filter(w => /^[A-Za-zÀ-ÿ]/.test(w) && !['de', 'for', 'a', 'the'].includes(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
}